import apiClient from './client'
import type {
  Podcast,
  PodcastEpisode,
  CreatePodcastRequest,
  UpdatePodcastRequest,
  PublishEpisodeRequest,
  ApiResponse,
} from '@/types/models'
import {
  adaptPodcastResponse,
  adaptEpisodeResponse,
  adaptPodcastRequest,
  adaptPublishEpisodeRequest,
} from '@/lib/utils/api-adapter'

/**
 * TTS Podcast API endpoints (rivulet API >= v0.5.0).
 *
 * Episode publishing/regeneration is asynchronous: the API responds 202 and
 * synthesis runs server-side (a long article takes minutes). Poll
 * listEpisodes() until the episode leaves "pending".
 */
export const podcastsApi = {
  /**
   * List all podcasts for the current user
   */
  async list(): Promise<Podcast[]> {
    const response = await apiClient.get<any>('/api/v1/podcasts')
    return (response.podcasts || []).map(adaptPodcastResponse)
  },

  /**
   * Get a single podcast by ID
   */
  async get(id: string): Promise<ApiResponse<Podcast>> {
    const response = await apiClient.get<any>(`/api/v1/podcasts/${id}`)
    return { data: adaptPodcastResponse(response) }
  },

  /**
   * Create a new podcast
   */
  async create(data: CreatePodcastRequest): Promise<ApiResponse<Podcast>> {
    const response = await apiClient.post<any>(
      '/api/v1/podcasts',
      adaptPodcastRequest(data)
    )
    return { data: adaptPodcastResponse(response) }
  },

  /**
   * Update an existing podcast (partial; omitted fields unchanged)
   */
  async update(id: string, data: UpdatePodcastRequest): Promise<ApiResponse<Podcast>> {
    const response = await apiClient.put<any>(
      `/api/v1/podcasts/${id}`,
      adaptPodcastRequest(data)
    )
    return { data: adaptPodcastResponse(response) }
  },

  /**
   * Delete a podcast, its episodes, and their audio
   */
  async delete(id: string): Promise<void> {
    return apiClient.delete(`/api/v1/podcasts/${id}`)
  },

  /**
   * List a podcast's episodes, newest first
   */
  async listEpisodes(id: string): Promise<PodcastEpisode[]> {
    const response = await apiClient.get<any>(`/api/v1/podcasts/${id}/episodes`)
    return (response.episodes || []).map(adaptEpisodeResponse)
  },

  /**
   * Publish an existing item as an episode (async — returns 202; poll
   * listEpisodes for pending → ready/failed)
   */
  async publishEpisode(
    id: string,
    data: PublishEpisodeRequest
  ): Promise<{ status: string; message: string }> {
    return apiClient.post(
      `/api/v1/podcasts/${id}/episodes`,
      adaptPublishEpisodeRequest(data)
    )
  },

  /**
   * Re-run TTS for an episode from its source item (async — returns 202)
   */
  async regenerateEpisode(
    id: string,
    episodeId: string,
    data?: Omit<PublishEpisodeRequest, 'itemId'>
  ): Promise<{ status: string; message: string }> {
    const body: any = {}
    if (data?.source) body.source = data.source
    if (data?.voice) body.voice = data.voice
    if (data?.speed) body.speed = data.speed
    return apiClient.post(
      `/api/v1/podcasts/${id}/episodes/${episodeId}/regenerate`,
      body
    )
  },

  /**
   * Delete an episode and its audio
   */
  async deleteEpisode(id: string, episodeId: string): Promise<void> {
    return apiClient.delete(`/api/v1/podcasts/${id}/episodes/${episodeId}`)
  },
}

export default podcastsApi
